import dynamic from 'next/dynamic';
import styled from 'styled-components';

import { Checkmark } from 'shared/icons/Checkmark';
import { t } from 'localization/utils/t';
import { useIsNewMobilePdpAccessible } from 'shared/hooks/useIsNewMobilePdpAccessible';
import { useIsProductTemplateWanted } from 'wants/hooks/useIsProductTemplateWanted';
import { useWindowState } from 'productTemplate/hooks/useWindowState';

const TP = 'productTemplate.components.windows.FactsWindow';

const WantsWindow = dynamic(() => import('productTemplate/components/windows/WantsWindow'), {
  ssr: false,
});

const ClientOnlyPortal = dynamic(
  () => import('shared/components/ClientOnlyPortal').then((mod) => mod.ClientOnlyPortal),
  {
    ssr: false,
  },
);

interface IWantProductTemplateButton {
  color?: 'black' | 'white';
  selector?: string;
  eventContextOverride?: string;
}

export const WantProductTemplateButton = ({
  color = 'black',
  selector = '#main-page-layout',
  eventContextOverride = null,
}: IWantProductTemplateButton) => {
  const {
    windowState: wantsWindowState,
    handleLinkClick: handleWantsClick,
    handleLinkClose: handleWantsClose,
  } = useWindowState();
  const isNewMobilePdpAccessible = useIsNewMobilePdpAccessible();

  const showWantsCheckmark = useIsProductTemplateWanted();

  return (
    <WantProductTemplateButton.Wrapper $color={color}>
      <button
        data-qa="wants_button"
        onClick={handleWantsClick}
        type="button"
        className={`wantsButton ${isNewMobilePdpAccessible && 'rounded'}`}
      >
        {t(`${TP}.want`, 'Want')}
        {showWantsCheckmark && <Checkmark className="wantsCheckMark" color={color} />}
      </button>
      {wantsWindowState.isOpen && (
        <ClientOnlyPortal selector={selector}>
          <WantsWindow
            isPermanent={wantsWindowState.isPermanent}
            eventContextOverride={eventContextOverride}
            onClose={handleWantsClose}
          />
        </ClientOnlyPortal>
      )}
    </WantProductTemplateButton.Wrapper>
  );
};

WantProductTemplateButton.Wrapper = styled.div<{ $color: string }>`
  .wantsCheckMark {
    margin: 0 0 2px 4px;
  }

  .wantsButton {
    padding: 6px 4px 4px;
    border: ${({ theme, $color }) =>
      $color === 'white' ? theme.borders.opaqueWhite : theme.borders.black};
    align-self: flex-end;
    display: flex;
    height: 19px;
    justify-content: center;
    align-items: center;

    &.rounded {
      border-radius: ${({ theme }) => theme.borderRadii.button};
    }
  }
`;
